import { RootState } from '@/plugins/store';
import { ActionTree } from 'vuex';
import { DocumentReglementaireState, state } from './state';
import {
    DocumentReglementaireActions,
    DocumentReglementaireActionTypes
} from '@/modules/DocumentReglementaire/store/action-types';
import DocumentReglementaireService, {
    DocumentReglementaireToDownload,
    FilterDocumentsReglementaire
} from '@/modules/DocumentReglementaire/services/DocumentReglementaireService';
import { DOCUMENT_REGLEMENTAIRE } from '@/modules/DocumentReglementaire/store/mutation-types';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import ErrorManager from '@/common/utils/ErrorManager';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import { DocumentTypeCode } from '@/models/TypeDocumentReglementaire';

export const actions: ActionTree<DocumentReglementaireState, RootState> = {
    async [DocumentReglementaireActions.GET_TYPES_DOCUMENTS_REGLEMENTAIRES](
        { commit }: DocumentReglementaireActionTypes,
        payload: { isBim: boolean }
    ) {
        if (state.typesDocumentsReglementaires && state.typesDocumentsReglementaires.length > 0) {
            return;
        }
        if (payload.isBim) {
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_LIST_TYPES_IS_LOADING}`, true, {
                root: true
            });
        } else {
            commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, undefined);
        }
        try {
            const response = await DocumentReglementaireService.getTypesDocumentReglementaire();
            if (response.status === 200 && response.data !== undefined) {
                commit(DOCUMENT_REGLEMENTAIRE.SET_TYPES_DOCUMENTS, response.data != undefined ? response.data : []);
                if (payload.isBim) {
                    commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_LIST_TYPES_IS_LOADING}`, false, {
                        root: true
                    });
                }
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        } catch (error) {
            if (payload.isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_LIST_TYPES_IS_LOADING}`, false, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, error);
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },

    async [DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE](
        { commit }: DocumentReglementaireActionTypes,
        payload: FilterDocumentsReglementaire
    ) {
        const { isBim, ...params } = payload;
        const serviceParams = params;
        delete (serviceParams as any).isBim;
        delete serviceParams.typeDocumentsReglementaireCodes;
        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_PAGE}`, payload.page, { root: true });
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_IS_LOADING}`, true, { root: true });
        } else {
            commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, undefined);
            commit(DOCUMENT_REGLEMENTAIRE.SET_IS_LOADING, true);
            commit(DOCUMENT_REGLEMENTAIRE.SET_PAGE, payload.page);
        }
        try {
            let response;
            const isAnalyseRisque =
                payload.typeDocumentsReglementaireCodes?.length === 1 &&
                payload.typeDocumentsReglementaireCodes[0] === DocumentTypeCode.ANALYSE_RISQUE;
            const isPriseEnCharge =
                payload.typeDocumentsReglementaireCodes?.length === 1 &&
                payload.typeDocumentsReglementaireCodes[0] === DocumentTypeCode.PRISE_EN_CHARGE;
            if (isPriseEnCharge) {
                response = await DocumentReglementaireService.getPrisesEnCharge(serviceParams);
            } else if (isAnalyseRisque) {
                response = await DocumentReglementaireService.getAnalysesDeRisques(serviceParams);
            } else {
                response = await DocumentReglementaireService.getDocumentReglementaire(serviceParams);
            }
            if (response.status === 200 && response.data !== undefined) {
                if (payload.isForModal) {
                    commit(DOCUMENT_REGLEMENTAIRE.SET_COUNT_FOR_MODAL, response.headers.count);
                } else {
                    if (isPriseEnCharge) {
                        const prisesEnCharge = Array.isArray(response.data) ? response.data : [];
                        if (isBim) {
                            commit(
                                `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_PEC_ITEMS}`,
                                prisesEnCharge,
                                { root: true }
                            );
                            commit(
                                `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_NB_OF_PAGES}`,
                                1,
                                { root: true }
                            );
                            commit(
                                `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_COUNT}`,
                                prisesEnCharge.length,
                                { root: true }
                            );
                        } else {
                            commit(DOCUMENT_REGLEMENTAIRE.SET_ITEMS_PEC, prisesEnCharge);
                            commit(DOCUMENT_REGLEMENTAIRE.SET_NB_OF_PAGES, 1);
                            commit(DOCUMENT_REGLEMENTAIRE.SET_COUNT, prisesEnCharge.length);
                        }
                    } else if (isAnalyseRisque) {
                        if (payload.page === 0) {
                            if (isBim) {
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ADR_ITEMS}`,
                                    response.data.content != undefined ? response.data.content : [],
                                    {
                                        root: true
                                    }
                                );
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_NB_OF_PAGES}`,
                                    response.data.totalPages,
                                    { root: true }
                                );
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_COUNT}`,
                                    response.data.totalElements,
                                    {
                                        root: true
                                    }
                                );
                            } else {
                                commit(
                                    DOCUMENT_REGLEMENTAIRE.SET_ITEMS_ADR,
                                    response.data.content != undefined ? response.data.content : []
                                );
                                commit(DOCUMENT_REGLEMENTAIRE.SET_SCROLL, 0);
                                commit(DOCUMENT_REGLEMENTAIRE.SET_NB_OF_PAGES, response.data.totalPages);
                                commit(DOCUMENT_REGLEMENTAIRE.SET_COUNT, response.data.totalElements);
                            }
                        } else {
                            if (isBim) {
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_ADD_ADR_ITEMS}`,
                                    response.data.content,
                                    {
                                        root: true
                                    }
                                );
                            } else {
                                commit(DOCUMENT_REGLEMENTAIRE.ADD_ITEMS_ADR, response.data.content);
                            }
                        }
                    } else {
                        if (payload.page === 0) {
                            if (isBim) {
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ITEMS}`,
                                    response.data,
                                    {
                                        root: true
                                    }
                                );
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_NB_OF_PAGES}`,
                                    response.headers.pages,
                                    { root: true }
                                );
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_COUNT}`,
                                    response.headers.count,
                                    {
                                        root: true
                                    }
                                );
                            } else {
                                commit(DOCUMENT_REGLEMENTAIRE.SET_ITEMS, response.data);
                                commit(DOCUMENT_REGLEMENTAIRE.SET_SCROLL, 0);
                                commit(DOCUMENT_REGLEMENTAIRE.SET_NB_OF_PAGES, response.headers.pages);
                                commit(DOCUMENT_REGLEMENTAIRE.SET_COUNT, response.headers.count);
                            }
                        } else {
                            if (isBim) {
                                commit(
                                    `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_ADD_ITEMS}`,
                                    response.data,
                                    {
                                        root: true
                                    }
                                );
                            } else {
                                commit(DOCUMENT_REGLEMENTAIRE.ADD_ITEMS, response.data);
                            }
                        }
                    }
                }
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_IS_LOADING}`, false, { root: true });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_IS_LOADING}`, false, { root: true });
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, error);
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_IS_LOADING, false);
                commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, error);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },

    async [DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE_PDF](
        { commit }: DocumentReglementaireActionTypes,
        payload: DocumentReglementaireToDownload
    ) {
        const { isBim, ...params } = payload;
        const serviceParams = params;
        delete (serviceParams as any).isBim;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, undefined, { root: true });
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, true, { root: true });
        } else {
            commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, undefined);
            commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, true);
        }
        try {
            const response = await DocumentReglementaireService.getDocumentReglementairePDF(serviceParams);
            if (response.status === 200 && response.data !== undefined) {
                let blob;
                if (payload.uniqueDocumentIds?.length === 1) {
                    if (payload.extension === 'jpg' || payload.extension === 'png') {
                        blob = new Blob([response.data], { type: 'image/jpeg' });
                    } else {
                        blob = new Blob([response.data], { type: 'application/pdf' });
                    }
                } else {
                    blob = new Blob([response.data], { type: 'application/zip' });
                }
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.target = 'blank';
                if (payload.uniqueDocumentIds?.length === 1) {
                    link.download = payload.nomDocument === undefined ? 'Documents réglementaire' : payload.nomDocument;
                } else {
                    link.download = payload.nomZip === undefined ? 'Documents réglementaires' : payload.nomZip;
                }
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, error, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, error);
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },
    async [DocumentReglementaireActions.GET_ALL_DOCUMENT_REGLEMENTAIRE_PDF](
        { commit }: DocumentReglementaireActionTypes,
        payload: FilterDocumentsReglementaire
    ) {
        const { isBim, ...params } = payload;
        const serviceParams = params;
        delete (serviceParams as any).isBim;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, undefined, { root: true });
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, true, { root: true });
        } else {
            commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, undefined);
            commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, true);
        }
        try {
            const response = await DocumentReglementaireService.getAllDocumentReglementairePDF(serviceParams);
            if (response.status === 200 && response.data !== undefined) {
                const blob = new Blob([response.data], { type: 'application/zip' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.target = 'blank';
                link.download = payload.nomZip === undefined ? 'Documents réglementaires' : payload.nomZip;
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, error, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, error);
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },
    async [DocumentReglementaireActions.GET_ADR_PDF](
        { commit }: DocumentReglementaireActionTypes,
        payload: DocumentReglementaireToDownload
    ) {
        const { isBim, ...params } = payload;
        const serviceParams = params;
        delete (serviceParams as any).isBim;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, true, {
                root: true
            });
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, undefined, {
                root: true
            });
        } else {
            commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, undefined);
            commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, true);
        }
        try {
            const response = await DocumentReglementaireService.getAdrPDF(serviceParams);
            if (response.status === 200 && response.data !== undefined) {
                let blob;
                if (payload.uniqueDocumentIds?.length === 1) {
                    blob = new Blob([response.data], { type: 'application/pdf' });
                } else {
                    blob = new Blob([response.data], { type: 'application/zip' });
                }
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.target = 'blank';
                if (payload.uniqueDocumentIds?.length === 1) {
                    link.download = payload.nomDocument === undefined ? 'Analyse de risque' : payload.nomDocument;
                } else {
                    link.download = payload.nomZip === undefined ? 'Analyses de risques' : payload.nomZip;
                }
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, error, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, error);
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },

    async [DocumentReglementaireActions.GET_ALL_ADR_PDF](
        { commit }: DocumentReglementaireActionTypes,
        payload: FilterDocumentsReglementaire
    ) {
        const { isBim, ...params } = payload;
        const serviceParams = params;
        delete (serviceParams as any).isBim;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, true, {
                root: true
            });
            commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, undefined, {
                root: true
            });
        } else {
            commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, undefined);
            commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, true);
        }
        try {
            const response = await DocumentReglementaireService.getAllAdrPDF(serviceParams);
            if (response.status === 200 && response.data !== undefined) {
                const blob = new Blob([response.data], { type: 'application/zip' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.target = 'blank';
                link.download = payload.nomZip === undefined ? 'Analyses de risques' : payload.nomZip;
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, false, {
                    root: true
                });
                commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ERROR}`, error, {
                    root: true
                });
            } else {
                commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, error);
                commit(DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },
    async [DocumentReglementaireActions.GET_PRISE_EN_CHARGE_PDF](
        { commit }: DocumentReglementaireActionTypes,
        { idDocExterne, idVersionExterne }: { idDocExterne: string | number; idVersionExterne: string | number }
    ) {
        // 1. Réinitialisation des erreurs et activation de l'état de chargement si disponible
        commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, undefined);

        try {
            const response = await DocumentReglementaireService.getPdfPriseEnCharge(
                idDocExterne,
                idVersionExterne
            );

            // 2. Vérification de la réponse HTTP et des données
            if (response.status === 200 && response.data) {
                const blob = new Blob([response.data], {
                    type: 'application/pdf'
                });

                // 3. Extraction robuste du nom de fichier depuis Content-Disposition
                const contentDisposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
                let fileName = `prise_en_charge_${idDocExterne}.pdf`;

                if (contentDisposition) {
                    // Prise en compte de filename* (RFC 5987/6266) et filename standard
                    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
                    const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

                    if (utf8Match && utf8Match[1]) {
                        fileName = decodeURIComponent(utf8Match[1]);
                    } else if (standardMatch && standardMatch[1]) {
                        fileName = standardMatch[1];
                    }
                }

                // 4. Déclenchement du téléchargement via le DOM
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();

                // Nettoyage
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // Réponse HTTP non standard ou corps vide
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(
                        response.status,
                        'app.error.document_indisponible'
                    ),
                    { root: true }
                );
            }
        } catch (error) {
            // 5. Capture des erreurs réseau ou serveur et notification de l'utilisateur
            commit(DOCUMENT_REGLEMENTAIRE.SET_ERROR, error);
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processError(error),
                { root: true }
            );
        }
    }
};
